import { resolveManifestUrl } from '../utils.js';

type Manifest = Record<string, string>;

const getUnVersionedManifestPath = (path: string) =>
  path.replace(/\/\d+\.\d+/, '/latest');

type GetOptions = {
  hostUrl: string;
  manifestPath: string;
};

const isValidHttpUrl = (fileName: string) => {
  let url;
  try {
    url = new URL(fileName);
  } catch (_) {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

const manifestCache = new Map<string, Manifest>();

const clearManifestCache = () => manifestCache.clear();

const get = async ({
  hostUrl,
  manifestPath,
}: GetOptions): Promise<Manifest> => {
  const manifestUrl = resolveManifestUrl(hostUrl, manifestPath);

  const cached = manifestCache.get(manifestUrl);
  if (cached) return cached;

  const fetchManifest = async (fetchUrl: string) => {
    const response = await fetch(fetchUrl);
    if (!response.ok) {
      throw new Error(`unable to get manifest ${fetchUrl}`);
    }
    const contentType = response.headers?.get?.('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      throw new Error(`manifest ${fetchUrl} is not a valid json`);
    }
    const data = (await response.json()) as Manifest;
    manifestCache.set(fetchUrl, data);
    return data;
  };

  try {
    return await fetchManifest(manifestUrl);
  } catch (error) {
    const unVersionedManifestPath = getUnVersionedManifestPath(manifestPath);
    if (manifestPath !== unVersionedManifestPath) {
      const fallbackUrl = resolveManifestUrl(hostUrl, unVersionedManifestPath);
      return await fetchManifest(fallbackUrl);
    }
    throw error;
  }
};

const getFullFileNameofAssets = (
  manifest: Record<string, string>,
  assetNames: Array<string> = [],
): Array<string> =>
  assetNames.reduce((assets: Array<string>, assetName) => {
    const fullFileName = manifest[assetName];
    if (fullFileName) assets.push(fullFileName);
    else if (isValidHttpUrl(assetName)) assets.push(assetName);
    else {
      throw new Error(`unable to locate ${assetName} in manifest`);
    }
    return assets;
  }, []);

export const ManifestLoader = {
  get,
  getFullFileNameofAssets,
  clearCache: clearManifestCache,
};
